import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter, Subscription} from 'rxjs';
import {environment} from '../../../environments/environment';

type SiteAnalyticsEventType = 'PAGE_VIEW' | 'PAGE_EXIT' | 'ACTION';

interface SiteAnalyticsEventPayload {
  type: SiteAnalyticsEventType;
  pageKey: string;
  referrerPageKey?: string | null;
  actionKey?: string | null;
  occurredAt: string;
  durationSeconds?: number | null;
}

interface SiteAnalyticsBatchPayload {
  visitorId: string;
  events: SiteAnalyticsEventPayload[];
}

@Injectable({
  providedIn: 'root'
})
export class SiteAnalyticsService implements OnDestroy {
  private readonly visitorStorageKey = 'freyr-anonymous-visitor-id';
  private readonly disabledStorageKey = 'freyr-site-analytics-disabled';
  private readonly flushThreshold = 3;
  private readonly flushIntervalMs = 2000;

  private started = false;
  private queue: SiteAnalyticsEventPayload[] = [];
  private routeSubscription?: Subscription;
  private flushTimerId: number | null = null;
  private currentPageKey: string | null = null;
  private currentPageEnteredAtMs = 0;
  private visitorId: string | null = null;

  constructor(
    private router: Router,
    private ngZone: NgZone
  ) {}

  start(): void {
    if (this.started || typeof window === 'undefined' || this.isTrackingDisabled()) {
      return;
    }

    this.started = true;
    this.visitorId = this.getOrCreateVisitorId();
    if (!this.visitorId) {
      return;
    }

    const initialPage = this.normalizePageKey(this.router.url || '/');
    if (initialPage) {
      this.currentPageKey = initialPage;
      this.currentPageEnteredAtMs = Date.now();
      this.enqueue({
        type: 'PAGE_VIEW',
        pageKey: initialPage,
        occurredAt: new Date().toISOString()
      });
    }

    this.routeSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nextPage = this.normalizePageKey(event.urlAfterRedirects || event.url || '/');
        if (!nextPage) {
          return;
        }

        const previousPage = this.currentPageKey;
        if (previousPage) {
          this.enqueuePageExit(previousPage);
        }

        this.currentPageKey = nextPage;
        this.currentPageEnteredAtMs = Date.now();
        this.enqueue({
          type: 'PAGE_VIEW',
          pageKey: nextPage,
          referrerPageKey: previousPage,
          occurredAt: new Date().toISOString()
        });
      });

    this.ngZone.runOutsideAngular(() => {
      this.flushTimerId = window.setInterval(() => this.flush(false), this.flushIntervalMs);
      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('beforeunload', this.handlePageHide);
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.clearFlushTimer();
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.handlePageHide);
      window.removeEventListener('beforeunload', this.handlePageHide);
    }
  }

  trackAction(actionKey: string, pageKey?: string | null, flushImmediately = false): void {
    if (!this.started || !this.visitorId) {
      return;
    }
    const normalizedPage = this.normalizePageKey(pageKey ?? this.currentPageKey ?? this.router.url ?? '/');
    const normalizedAction = this.normalizeActionKey(actionKey);
    if (!normalizedPage || !normalizedAction) {
      return;
    }
    this.enqueue({
      type: 'ACTION',
      pageKey: normalizedPage,
      actionKey: normalizedAction,
      occurredAt: new Date().toISOString()
    });

    if (flushImmediately) {
      this.flush(false);
    }
  }

  private enqueuePageExit(pageKey: string): void {
    const now = Date.now();
    const durationSeconds = Math.max(0, Math.round((now - this.currentPageEnteredAtMs) / 1000));
    this.enqueue({
      type: 'PAGE_EXIT',
      pageKey,
      occurredAt: new Date(now).toISOString(),
      durationSeconds
    });
  }

  private enqueue(event: SiteAnalyticsEventPayload): void {
    this.queue.push(event);
    if (this.queue.length >= this.flushThreshold) {
      this.flush(false);
    }
  }

  private flush(useBeacon: boolean): void {
    if (!this.visitorId || this.queue.length === 0) {
      return;
    }

    const payload: SiteAnalyticsBatchPayload = {
      visitorId: this.visitorId,
      events: this.queue.splice(0, this.queue.length)
    };

    const url = this.getCollectUrl();
    const body = JSON.stringify(payload);

    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], {type: 'application/json'});
      const sent = navigator.sendBeacon(url, blob);
      if (sent) {
        return;
      }
    }

    void fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      keepalive: useBeacon,
      body
    }).catch(() => undefined);
  }

  private readonly handlePageHide = () => {
    if (!this.started || !this.visitorId) {
      return;
    }
    if (this.currentPageKey) {
      this.enqueuePageExit(this.currentPageKey);
      this.currentPageKey = null;
    }
    this.flush(true);
  };

  private getCollectUrl(): string {
    const base = environment.securityApiUrl || environment.apiUrl.replace(/\/api\/?$/, '');
    return `${base}/site-analytics/collect`;
  }

  private getOrCreateVisitorId(): string | null {
    try {
      const stored = localStorage.getItem(this.visitorStorageKey);
      if (stored && stored.trim().length >= 16) {
        return stored.trim();
      }
      const generated = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
      localStorage.setItem(this.visitorStorageKey, generated);
      return generated;
    } catch {
      return null;
    }
  }

  private isTrackingDisabled(): boolean {
    try {
      if (localStorage.getItem(this.disabledStorageKey) === '1') {
        return true;
      }
    } catch {
      // ignore
    }

    if (typeof navigator !== 'undefined') {
      const doNotTrack = (navigator as Navigator & { msDoNotTrack?: string }).doNotTrack
        || (window as Window & { doNotTrack?: string }).doNotTrack
        || (navigator as Navigator & { msDoNotTrack?: string }).msDoNotTrack;
      if (doNotTrack === '1' || doNotTrack === 'yes') {
        return true;
      }
    }

    return false;
  }

  private normalizePageKey(value: string | null | undefined): string | null {
    const raw = (value ?? '').trim();
    if (!raw) {
      return null;
    }

    const path = raw
      .replace(/^https?:\/\/[^/]+/i, '')
      .split('?')[0]
      .split('#')[0]
      .trim();

    const normalized = path
      .replace(/\/dashboard\/overlays\/[^/]+$/i, '/dashboard/overlays/:type')
      .replace(/\/+/g, '/')
      .replace(/\/$/, '') || '/';

    return normalized.toLowerCase();
  }

  private normalizeActionKey(value: string | null | undefined): string | null {
    const raw = (value ?? '').trim().toLowerCase();
    if (!raw) {
      return null;
    }
    return raw.replace(/[^a-z0-9:_/-]/g, '').slice(0, 128) || null;
  }

  private clearFlushTimer(): void {
    if (this.flushTimerId != null) {
      window.clearInterval(this.flushTimerId);
      this.flushTimerId = null;
    }
  }
}
